/**
 * "Übersicht der Vertretungsanfragen" (office, /substitution-inquiries-table): texts and grouping of the redesigned page.
 * The situation of each inquiry comes from the substitution-manager (GET overview, src/overview/overview.rules.ts);
 * here: tiles, sections, blocks of one sick report, row texts, countdowns, time chips, the "Verlauf" of the panel and the
 * way of suggested teachers. Pure (moment only), checked in tests/substitutions/overview.check.ts.
 */
import moment from 'moment';
import {confirmationChannelText, sentConfirmationChannel} from '@/helpers/substitutions/confirmationChannel';
import {askedLater, requestTimeOf, revokeTimeOf} from '@/helpers/substitutions/legacyTimes';
import {
  SUBSTITUTION_OVERVIEW_GROUP_ORDER,
  SubstitutionOverviewGroup,
  SubstitutionOverviewSituation,
} from '@/enums/substitution-manager/SubstitutionOverview';
import {
  SubstitutionOverviewItem,
  SubstitutionOverviewPerson,
  SubstitutionOverviewSickReport,
} from '@/classes/dto/substitution-manager/overview/SubstitutionOverview.dto';

moment.locale('de');

const S = SubstitutionOverviewSituation;
const G = SubstitutionOverviewGroup;

export type OverviewTone = 'bad' | 'warn' | 'info' | 'good' | 'comp' | 'muted';

export interface SituationMeta {
  /** Status pill / tile title */
  label: string;
  /** Short pill on the phone */
  short: string;
  tone: OverviewTone;
  /** Phrase in the summary of a sick report block ("1 ohne Vertretung") */
  summary: string;
}

export const SITUATION_META: Record<SubstitutionOverviewSituation, SituationMeta> = {
  [S.NOT_SENT]: {label: 'Noch nicht versendet', short: 'Nicht versendet', tone: 'warn', summary: 'nicht versendet'},
  [S.REPORTED_ONLY]: {label: 'Ausfall gemeldet – noch keine Suche', short: 'Noch keine Suche', tone: 'warn', summary: 'noch keine Suche'},
  [S.NO_CANDIDATES]: {label: 'Keine Vertretung gefunden', short: 'Keine Vertretung', tone: 'bad', summary: 'ohne Vertretung'},
  [S.ALL_DECLINED]: {label: 'Alle haben abgesagt', short: 'Alle abgesagt', tone: 'bad', summary: 'alle abgesagt'},
  [S.WITHDRAWN]: {label: 'Zusage zurückgezogen', short: 'Zurückgezogen', tone: 'warn', summary: 'zurückgezogen'},
  [S.CHOOSE]: {label: 'Zusage da – bitte auswählen', short: 'Auswahl nötig', tone: 'warn', summary: 'Auswahl nötig'},
  [S.CANCEL_NOT_SENT]: {label: 'Absage noch nicht versendet', short: 'Absage nicht versendet', tone: 'warn', summary: 'Absage nicht versendet'},
  [S.SEARCHING]: {label: 'Suche läuft', short: 'Suche läuft', tone: 'info', summary: 'Suche läuft'},
  [S.FOUND]: {label: 'Vertretung gefunden', short: 'Vertretung steht', tone: 'good', summary: 'Vertretung steht'},
  [S.COMPENSATION_OPEN]: {label: 'Kompensation offen', short: 'Kompensation offen', tone: 'comp', summary: 'Kompensation offen'},
  [S.CANCELLING]: {label: 'Absage wird versendet', short: 'Absage läuft', tone: 'muted', summary: 'Absage läuft'},
  [S.CANCELLED]: {label: 'Abgesagt', short: 'Abgesagt', tone: 'muted', summary: 'abgesagt'},
  [S.PAST]: {label: 'Vorbei – ohne Vertretung', short: 'Vorbei', tone: 'muted', summary: 'vorbei'},
};

export const GROUP_META: Record<SubstitutionOverviewGroup, { label: string; short: string; tone: OverviewTone }> = {
  [G.ACTION]: {label: 'Handeln nötig', short: 'Handeln nötig', tone: 'bad'},
  [G.SEARCHING]: {label: 'Suche läuft', short: 'Suche läuft', tone: 'info'},
  [G.FOUND]: {label: 'Vertretung steht', short: 'Steht', tone: 'good'},
  [G.COMPENSATION]: {label: 'Abgesagt · Kompensation offen', short: 'Kompensation', tone: 'comp'},
  [G.DONE]: {label: 'Erledigt', short: 'Erledigt', tone: 'muted'},
};

/* ================================================================ tiles */

export interface TileDef {
  situation: SubstitutionOverviewSituation;
  label: string;
  tone: OverviewTone;
}

/** Counter tiles in this order */
export const TILE_DEFS: TileDef[] = [
  {situation: S.NO_CANDIDATES, label: 'Keine Vertretung gefunden', tone: 'bad'},
  {situation: S.ALL_DECLINED, label: 'Alle haben abgesagt', tone: 'bad'},
  {situation: S.WITHDRAWN, label: 'Zusage zurückgezogen', tone: 'warn'},
  {situation: S.NOT_SENT, label: 'Noch nicht versendet', tone: 'warn'},
  {situation: S.REPORTED_ONLY, label: 'Ausfall gemeldet – noch keine Suche', tone: 'warn'},
  {situation: S.SEARCHING, label: 'Suche läuft', tone: 'info'},
  {situation: S.COMPENSATION_OPEN, label: 'Kompensation offen', tone: 'comp'},
];

export interface Tile extends TileDef {
  count: number;
  hint: string;
  active: boolean;
}

/** Short explanation under the number (true for any count) */
export function tileHint(situation: SubstitutionOverviewSituation, count: number): string {
  switch (situation) {
    case S.NO_CANDIDATES:
      return 'niemand konnte angefragt werden';
    case S.ALL_DECLINED:
      return 'alle Angefragten haben abgesagt';
    case S.WITHDRAWN:
      return 'niemand mehr offen';
    case S.NOT_SENT:
      return 'Anfrage wartet auf Versand';
    case S.REPORTED_ONLY:
      return 'Büro entscheidet – keine automatische Absage';
    case S.SEARCHING:
      return count ? 'Antworten stehen aus' : 'niemand wartet auf Antwort';
    case S.COMPENSATION_OPEN:
      return 'abgesagt, noch nicht entschieden – auch frühere';
    default:
      return '';
  }
}

/**
 * Tiles that make sense: every situation with inquiries, "Suche läuft" always (orientation), the selected one even
 * without inquiries (so it can be switched off)
 */
export function visibleTiles(counts: Partial<Record<SubstitutionOverviewSituation, number>>, selected: string[] | undefined): Tile[] {
  const chosen = new Set(selected || []);
  return TILE_DEFS
    .map((def) => ({...def, count: counts[def.situation] || 0, hint: tileHint(def.situation, counts[def.situation] || 0), active: chosen.has(def.situation)}))
    .filter((tile) => tile.count > 0 || tile.active || tile.situation === S.SEARCHING);
}

/* ================================================================ names, dates */

export function personName(person: SubstitutionOverviewPerson | { firstName?: string; lastName?: string } | null | undefined, fallback = 'Unbekannt'): string {
  const name = `${person?.firstName || ''} ${person?.lastName || ''}`.trim();
  return name || fallback;
}

/** "DD" / "MK" ("?" without name) */
export function initials(person: { firstName?: string; lastName?: string } | null | undefined): string {
  const text = `${(person?.firstName || '').trim().charAt(0)}${(person?.lastName || '').trim().charAt(0)}`.toUpperCase();
  return text || '?';
}

/** "Di 13.10." */
export function dayText(day: Date | string): string {
  return moment(day).format('dd DD.MM.');
}

/** "06:35–07:05" ('' without time) */
export function timeRangeText(range: { startTime: number; endTime: number } | null | undefined): string {
  if (!range || typeof range.startTime !== 'number') {
    return '';
  }
  const fmt = (minutes: number) => moment().startOf('day').add(minutes, 'minutes').format('HH:mm');
  return `${fmt(range.startTime)}–${fmt(range.endTime)}`;
}

/** "So 11:15" within a week, else "11.10. 11:15" */
export function shortMoment(date: Date | string, now: Date): string {
  const m = moment(date);
  return Math.abs(m.diff(moment(now), 'days')) < 6 ? m.format('dd HH:mm') : m.format('DD.MM. HH:mm');
}

/** "in 1 Tag 16 Std." / "in 4 Tagen" / "in 2 Std. 5 Min." / "in 25 Min." / "jetzt" */
export function countdownIn(target: Date | string, now: Date): string {
  const minutes = Math.ceil(moment(target).diff(moment(now), 'seconds') / 60);
  if (minutes <= 0) {
    return 'jetzt';
  }
  const days = Math.floor(minutes / (24 * 60));
  const hours = Math.floor((minutes % (24 * 60)) / 60);
  const rest = minutes % 60;
  if (days >= 2) {
    return `in ${days} Tagen`;
  }
  if (days === 1) {
    return `in 1 Tag${hours ? ` ${hours} Std.` : ''}`;
  }
  return `in ${[hours ? `${hours} Std.` : '', rest ? `${rest} Min.` : ''].filter(Boolean).join(' ')}`;
}

/** "Di 13.10., 03:35 Uhr" */
export function dateTimeText(date: Date | string): string {
  return `${moment(date).format('dd DD.MM., HH:mm')} Uhr`;
}

/** "nach 4 Min." / "nach 2 Std." / "nach 1 Tag" ('' without both times) */
export function responseTimeText(sentAt: Date | string | null | undefined, answeredAt: Date | string | null | undefined): string {
  if (!sentAt || !answeredAt) {
    return '';
  }
  // whole minutes passed ("13:31 → 13:34:37" = nach 3 Min.)
  const minutes = Math.max(0, Math.floor(moment(answeredAt).diff(moment(sentAt), 'seconds') / 60));
  if (minutes < 60) {
    return `nach ${Math.max(1, minutes)} Min.`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return `nach ${hours} Std.`;
  }
  const days = Math.round(hours / 24);
  return `nach ${days} ${days === 1 ? 'Tag' : 'Tagen'}`;
}

/** One asked teacher of the panel: state, time of the latest state and response time to the own request */
export interface AskedAnswer {
  text: string;
  when: string;
  tone: OverviewTone;
}

export interface AskedAnswerInquiry {
  requestSentAt?: Date | string | null;
  confirmations?: Array<{ employee?: unknown; confirmedAt?: Date | string | null; revokedAt?: Date | string | null; isRevoked?: boolean;
    isSelectedItem?: boolean; enteredVia?: string | null; updatedAt?: Date | string | null }>;
}

export interface AskedAnswerSubstitute {
  employee?: unknown;
  hasRejected?: boolean;
  rejectedAt?: Date | string | null;
  requestedAt?: Date | string | null;
  contactByMail?: boolean;
  contactByWhatsApp?: boolean;
  messengerMail?: unknown;
  messengerWhatsAppMessage?: unknown;
}

/**
 * "Vertritt · So 13:34 · nach 3 Min.": time of the latest state; response time from the own request (asked later:
 * requestedAt, else the first request) to the first answer (a withdrawn confirmation: its confirmedAt).
 */
export function askedAnswer(inquiry: AskedAnswerInquiry, substitute: AskedAnswerSubstitute, now: Date): AskedAnswer {
  const id = idText(substitute.employee);
  const own = (inquiry.confirmations || []).filter((c) => idText(c.employee) === id);
  const selected = own.find((c) => !c.isRevoked && c.isSelectedItem);
  const waiting = own.find((c) => !c.isRevoked && !c.isSelectedItem);
  const revoked = own.find((c) => c.isRevoked);
  const askedAt = requestTimeOf(substitute) || inquiry.requestSentAt;
  const when = (stateAt: Date | string | null | undefined, answeredAt: Date | string | null | undefined, entered?: boolean) => stateAt
    ? [shortMoment(stateAt, now), entered ? '' : responseTimeText(askedAt, answeredAt)].filter(Boolean).join(' · ') : '';
  if (selected) {
    return selected.enteredVia
      ? {text: 'Vertritt (vom Büro eingetragen)', when: when(selected.confirmedAt, undefined, true), tone: 'good'}
      : {text: 'Vertritt', when: when(selected.confirmedAt, selected.confirmedAt), tone: 'good'};
  }
  if (waiting) {
    return {text: 'Zugesagt – Warteliste', when: when(waiting.confirmedAt, waiting.confirmedAt), tone: 'warn'};
  }
  if (revoked) {
    return substitute.hasRejected
      ? {text: 'Zusage zurückgezogen', when: when(revokeTimeOf(revoked, inquiry.confirmations || [], substitute.rejectedAt), revoked.confirmedAt), tone: 'bad'}
      : {text: 'Vom Büro widerrufen', when: when(revokeTimeOf(revoked, inquiry.confirmations || []), revoked.confirmedAt), tone: 'muted'};
  }
  if (substitute.hasRejected) {
    return {text: 'Kann ich nicht', when: when(substitute.rejectedAt, substitute.rejectedAt), tone: 'bad'};
  }
  const contacted = substitute.contactByMail || substitute.contactByWhatsApp || substitute.messengerMail || substitute.messengerWhatsAppMessage;
  return {text: contacted ? 'Noch keine Antwort' : 'Nicht kontaktiert', when: '', tone: 'muted'};
}

/* ================================================================ rows */

export interface RowTexts {
  day: string;
  time: string;
  title: string;
  makeup: string;
  place: string;
  absence: string;
  openText: string;
  pill: string;
  pillShort: string;
  tone: OverviewTone;
  /** "Absage in 1 Tag 16 Std." (bold) */
  next: string;
  /** "Di 13.10., 03:35 Uhr" / explanation */
  nextSub: string;
  nextTone: OverviewTone;
}

export function kidsText(count: number | undefined): string {
  if (typeof count !== 'number') {
    return '';
  }
  return count === 1 ? '1 Kind' : `${count} Kinder`;
}

/** "Gitarre · KS015" / "2 Kurse · KS021, KS011" */
export function courseTitle(item: Pick<SubstitutionOverviewItem, 'courses' | 'courseCustomIds'>): string {
  const ids = item.courseCustomIds || [];
  if (ids.length > 1) {
    return `${ids.length} Kurse · ${ids.join(', ')}`;
  }
  const title = item.courses?.[0]?.title;
  return title ? `${title} · ${ids[0] || ''}` : (ids[0] || 'Kurs');
}

/** "Nachholtermin für Mi 07.10." ('' for regular lessons) */
export function makeupText(item: Pick<SubstitutionOverviewItem, 'courses'>): string {
  const sources = (item.courses || []).map((c) => c.makeupFor).filter(Boolean) as Array<Date | string>;
  if (!sources.length) {
    return '';
  }
  return `Nachholtermin für ${sources.map((d) => moment(d).format('dd DD.MM.')).join(', ')}`;
}

/** Selbst gemeldet (teacher area) */
export function isSelfReported(item: Pick<SubstitutionOverviewItem, 'source' | 'sickReport'>): boolean {
  return item.source === 'sickReport' || !!item.sickReport;
}

/** Meldung in the row / panel header ("· rechtzeitig") */
export const NOTICE_SHORT: Record<string, string> = {onTime: 'rechtzeitig', shortTerm: 'kurzfristig', late: 'nicht rechtzeitig'};

/**
 * Texts of a row. `reason`: name of the internal reason (taxonomy, if readable), e.g. "Krank".
 */
export function rowTexts(item: SubstitutionOverviewItem, now: Date, reason?: string): RowTexts {
  const meta = SITUATION_META[item.situation];
  const teacher = item.teacher ? personName(item.teacher, 'Lehrkraft') : 'keine reguläre Lehrkraft';
  const absenceParts = [`Ausfall: ${teacher}`];
  if (reason) {
    absenceParts.push(reason);
  }
  if (isSelfReported(item)) {
    absenceParts.push('selbst gemeldet');
  }
  if (item.noticeKind && NOTICE_SHORT[item.noticeKind]) {
    absenceParts.push(NOTICE_SHORT[item.noticeKind]);
  }
  if (item.situation === S.WITHDRAWN && item.withdrawnBy) {
    absenceParts.push(`${personName(item.withdrawnBy, 'Die Vertretung')} hat die Zusage zurückgezogen`);
  }
  if (item.situation === S.FOUND && item.substitute) {
    absenceParts.push(`vertritt ${personName(item.substitute, 'eine Lehrkraft')}`);
  }
  if (item.waitingCount && item.situation === S.FOUND) {
    absenceParts.push(`${item.waitingCount} auf der Warteliste`);
  }
  const place = [item.institution?.name || 'Standort', kidsText(item.studentCount)].filter(Boolean).join(' · ');
  const askedCount = (item.asked || []).length;
  const openText = askedCount && [S.SEARCHING, S.ALL_DECLINED, S.WITHDRAWN, S.NOT_SENT].includes(item.situation)
    ? `${item.openCount} von ${askedCount} offen` : '';
  let next = '';
  let nextSub = '';
  let nextTone: OverviewTone = 'muted';
  if (item.autoCancelAt && item.situation !== S.FOUND) {
    next = `Absage ${countdownIn(item.autoCancelAt, now)}`;
    nextSub = dateTimeText(item.autoCancelAt);
    nextTone = item.situation === S.SEARCHING ? 'info' : meta.tone;
  } else if (item.autoCancelStoppedAt && [S.NO_CANDIDATES, S.ALL_DECLINED, S.WITHDRAWN, S.SEARCHING].includes(item.situation)) {
    nextSub = 'Automatische Absage gestoppt · Büro entscheidet';
  } else if (item.situation === S.REPORTED_ONLY) {
    nextSub = 'Keine automatische Absage – Büro entscheidet';
  } else if (item.situation === S.NOT_SENT) {
    nextSub = 'Anfrage prüfen und senden';
  } else if (item.situation === S.CHOOSE) {
    nextSub = `${item.waitingCount} ${item.waitingCount === 1 ? 'Zusage' : 'Zusagen'} – bitte Vertretung auswählen`;
  } else if (item.situation === S.CANCEL_NOT_SENT) {
    nextSub = 'Absage prüfen und senden';
  } else if ([S.NO_CANDIDATES, S.ALL_DECLINED, S.WITHDRAWN].includes(item.situation)) {
    nextSub = 'Keine automatische Absage geplant';
  } else if (item.situation === S.COMPENSATION_OPEN || item.situation === S.CANCELLED) {
    nextSub = item.completedCancellationAt ? 'Eltern informiert' : '';
  }
  return {
    day: dayText(item.day),
    time: timeRangeText(item.timeRange),
    title: courseTitle(item),
    makeup: makeupText(item),
    place,
    absence: absenceParts.join(' · '),
    openText,
    pill: meta.label,
    pillShort: meta.short,
    tone: meta.tone,
    next,
    nextSub,
    nextTone,
  };
}

/* ================================================================ sections + sick report blocks */

export interface RowEntry {
  kind: 'row';
  key: string;
  item: SubstitutionOverviewItem;
}

export interface BlockEntry {
  kind: 'block';
  key: string;
  report: SubstitutionOverviewSickReport;
  items: SubstitutionOverviewItem[];
  title: string;
  summary: string;
}

export interface Section {
  group: SubstitutionOverviewGroup;
  label: string;
  tone: OverviewTone;
  /** Inquiries of the section (whole filter, not only this page) */
  count: number;
  entries: Array<RowEntry | BlockEntry>;
  /** Collapsed by default ("n weitere Absagen … anzeigen") */
  collapsible: boolean;
}

/** "Krankmeldung Lehrer Lämpel · Di 13.10. · 2 Stunden · gemeldet So 11:15" */
export function blockTitle(items: SubstitutionOverviewItem[], report: SubstitutionOverviewSickReport, now: Date): string {
  const days = items.map((i) => moment(i.day).valueOf());
  const first = moment(Math.min(...days));
  const last = moment(Math.max(...days));
  const when = first.isSame(last, 'day') ? first.format('dd DD.MM.') : `${first.format('dd DD.MM.')}–${last.format('dd DD.MM.')}`;
  const lessons = items.reduce((sum, i) => sum + Math.max(1, (i.courseCustomIds || []).length), 0);
  const teacher = items[0]?.teacher ? personName(items[0].teacher, 'Lehrkraft') : 'Lehrkraft';
  return [`Krankmeldung ${teacher}`, when, lessons === 1 ? '1 Stunde' : `${lessons} Stunden`,
    report.reportedAt ? `gemeldet ${shortMoment(report.reportedAt, now)}` : ''].filter(Boolean).join(' · ');
}

/** "1 ohne Vertretung · 1 alle abgesagt" */
export function blockSummary(items: SubstitutionOverviewItem[]): string {
  const counts = new Map<SubstitutionOverviewSituation, number>();
  items.forEach((i) => counts.set(i.situation, (counts.get(i.situation) || 0) + 1));
  return Array.from(counts.entries()).map(([situation, n]) => `${n} ${SITUATION_META[situation].summary}`).join(' · ');
}

/**
 * Lessons of one absence of a teacher: sick-report inquiries of the same absent teacher on the same or consecutive days
 * (also from several reports submitted minutes apart). Returns cluster key per inquiry id (only clusters of 2+).
 */
export function absenceClusters(items: SubstitutionOverviewItem[]): Map<string, string> {
  const out = new Map<string, string>();
  const byTeacher = new Map<string, SubstitutionOverviewItem[]>();
  items.filter((i) => isSelfReported(i) && i.teacher?._id).forEach((i) => {
    const key = String(i.teacher?._id);
    byTeacher.set(key, [...(byTeacher.get(key) || []), i]);
  });
  byTeacher.forEach((list, teacher) => {
    const sorted = [...list].sort((a, b) => moment(a.day).valueOf() - moment(b.day).valueOf());
    let cluster: SubstitutionOverviewItem[] = [];
    const flush = () => {
      if (cluster.length > 1) {
        const key = `${teacher}|${moment(cluster[0].day).format('YYYY-MM-DD')}`;
        cluster.forEach((i) => out.set(String(i._id), key));
      }
      cluster = [];
    };
    for (const item of sorted) {
      const last = cluster[cluster.length - 1];
      if (last && moment(item.day).startOf('day').diff(moment(last.day).startOf('day'), 'days') > 1) {
        flush();
      }
      cluster.push(item);
    }
    flush();
  });
  return out;
}

/** Earliest report of the lessons of a block (several reports of one absence) */
function firstReport(items: SubstitutionOverviewItem[]): SubstitutionOverviewSickReport {
  const reports = items.map((i) => i.sickReport).filter(Boolean) as SubstitutionOverviewSickReport[];
  return reports.sort((a, b) => moment(a.reportedAt).valueOf() - moment(b.reportedAt).valueOf())[0]
    || {_id: '', reportedAt: undefined as never, reason: undefined as never, lessonCount: items.length, from: items[0].day, to: items[items.length - 1].day};
}

/**
 * Sections in the fixed order with the rows of this page; the lessons of one absence of a teacher (sick reports, same or
 * consecutive days, 2 or more in a section) become one block at the place of its first lesson
 */
export function buildSections(items: SubstitutionOverviewItem[], groupCounts: Partial<Record<SubstitutionOverviewGroup, number>>, now: Date): Section[] {
  return SUBSTITUTION_OVERVIEW_GROUP_ORDER.map((group) => {
    const own = items.filter((i) => i.group === group);
    const clusters = absenceClusters(own);
    const entries: Array<RowEntry | BlockEntry> = [];
    const done = new Set<string>();
    for (const item of own) {
      const key = clusters.get(String(item._id));
      if (key) {
        if (!done.has(key)) {
          done.add(key);
          const block = own.filter((i) => clusters.get(String(i._id)) === key);
          const report = firstReport(block);
          entries.push({
            kind: 'block', key: `block-${group}-${key}`, report, items: block,
            title: blockTitle(block, report, now), summary: blockSummary(block),
          });
        }
        continue;
      }
      entries.push({kind: 'row', key: String(item._id), item});
    }
    return {
      group,
      label: GROUP_META[group].label,
      tone: GROUP_META[group].tone,
      count: Math.max(groupCounts[group] || 0, own.length),
      entries,
      collapsible: group === G.DONE,
    };
  }).filter((section) => section.entries.length > 0);
}

/** "3 weitere Absagen anzeigen" / "1 weiterer Eintrag anzeigen" */
export function collapsedText(section: Section): string {
  const n = section.entries.reduce((sum, e) => sum + (e.kind === 'block' ? e.items.length : 1), 0);
  return n === 1 ? '1 erledigte Anfrage anzeigen' : `${n} erledigte Anfragen anzeigen`;
}

/* ================================================================ time chips */

export type TimeChip = 'from-today' | 'today' | 'week' | 'next14' | 'range';

export interface DateFilter {
  dayStart?: Date | string;
  dayEnd?: Date | string;
}

/** Filter of a chip ("Zeitraum…" is chosen in the dialog) */
export function chipFilter(chip: Exclude<TimeChip, 'range'>, now: Date): DateFilter {
  const today = moment(now).startOf('day');
  switch (chip) {
    case 'today':
      return {dayStart: today.toDate(), dayEnd: moment(now).endOf('day').toDate()};
    case 'week':
      return {dayStart: today.toDate(), dayEnd: moment(now).endOf('isoWeek').toDate()};
    case 'next14':
      return {dayStart: today.toDate(), dayEnd: moment(now).add(13, 'days').endOf('day').toDate()};
    default:
      return {dayStart: today.toDate()};
  }
}

/** Chip matching the date filter ('range' for anything else, also without dates) */
export function activeChip(filter: DateFilter, now: Date): TimeChip {
  const same = (a?: Date | string, b?: Date | string) => (!a && !b) || (!!a && !!b && moment(a).isSame(moment(b), 'minute'));
  for (const chip of ['from-today', 'today', 'week', 'next14'] as const) {
    const f = chipFilter(chip, now);
    if (same(filter.dayStart, f.dayStart) && same(filter.dayEnd, f.dayEnd)) {
      return chip;
    }
  }
  return 'range';
}

/** "Ab heute, So 11.10." / "13.10.–20.10." / "Alle Tage" */
export function dateFilterText(filter: DateFilter, now: Date): string {
  const chip = activeChip(filter, now);
  if (chip === 'from-today') {
    return `Ab heute, ${moment(now).format('dd DD.MM.')}`;
  }
  if (chip === 'today') {
    return `Heute, ${moment(now).format('dd DD.MM.')}`;
  }
  if (filter.dayStart && filter.dayEnd) {
    return `${moment(filter.dayStart).format('DD.MM.YYYY')} – ${moment(filter.dayEnd).format('DD.MM.YYYY')}`;
  }
  if (filter.dayStart) {
    return `Ab ${moment(filter.dayStart).format('DD.MM.YYYY')}`;
  }
  if (filter.dayEnd) {
    return `Bis ${moment(filter.dayEnd).format('DD.MM.YYYY')}`;
  }
  return 'Alle Tage';
}

/**
 * Date filter so that an inquiry opened by link (?inquiry=) is in the list: a day before dayStart / after dayEnd opens
 * the range from that day (undefined: already visible)
 */
export function dateFilterShowing(filter: DateFilter, day: Date | string): DateFilter | undefined {
  const d = moment(day);
  const beforeStart = !!filter.dayStart && d.isBefore(moment(filter.dayStart), 'day');
  const afterEnd = !!filter.dayEnd && d.isAfter(moment(filter.dayEnd), 'day');
  if (!beforeStart && !afterEnd) {
    return undefined;
  }
  return {
    ...(filter.dayStart ? {dayStart: (beforeStart ? d.clone().startOf('day') : moment(filter.dayStart)).toDate()} : {}),
    ...(filter.dayEnd && !afterEnd ? {dayEnd: moment(filter.dayEnd).toDate()} : {}),
  };
}

/* ================================================================ way of a suggested teacher */

/**
 * Office wording of the way to the Standort (same tiers as the substitution-manager travel hint, third person):
 * "Ganz in der Nähe – ca. 2 km (ca. 6 Min. mit dem Auto)", "Kurze Fahrt: ca. 4 km (ca. 8 Min. mit dem Auto)",
 * "ca. 18 km – ca. 22 Min. mit dem Auto"; without time "ca. 4 km Luftlinie"; '' without distance
 */
export function officeWayText(meters: number | null | undefined, seconds?: number | null, mode?: string | null, straightLine = false): string {
  if (typeof meters !== 'number' || !Number.isFinite(meters) || meters < 0) {
    return '';
  }
  const km = meters / 1000;
  const distance = meters < 1000 ? 'unter 1 km' : `ca. ${Math.round(km)} km`;
  if (straightLine || typeof seconds !== 'number' || !(seconds > 0)) {
    if (km <= 3) {
      return `Ganz in der Nähe – ${distance} Luftlinie`;
    }
    return km < 10 ? `Kurze Strecke: ${distance} Luftlinie` : `${distance} Luftlinie`;
  }
  const minutes = Math.max(1, Math.round(seconds / 60));
  const duration = minutes < 60 ? `ca. ${minutes} Min.` : `ca. ${Math.floor(minutes / 60)} Std.${minutes % 60 ? ` ${minutes % 60} Min.` : ''}`;
  const how = {transit: 'mit Bus und Bahn', bicycling: 'mit dem Rad', walking: 'zu Fuß'}[(mode || '').toLowerCase()] || 'mit dem Auto';
  const time = `${duration} ${how}`;
  if (km <= 3) {
    return `Ganz in der Nähe – ${distance} (${time})`;
  }
  if (km < 10 && seconds <= 20 * 60) {
    return `${(mode || '').toLowerCase() === 'walking' ? 'Kurzer Weg' : 'Kurze Fahrt'}: ${distance} (${time})`;
  }
  return `${distance} – ${time}`;
}

/* ================================================================ Verlauf (panel) */

export interface TimelineEntry {
  at: Date;
  text: string;
  /** Planned (future) step */
  planned?: boolean;
  tone?: OverviewTone;
}

type Id = unknown;
const idText = (value: Id): string => {
  const id = value && typeof value === 'object' && '_id' in (value as object) ? (value as { _id: unknown })._id : value;
  return id === undefined || id === null ? '' : String(id);
};

export interface TimelineInquiry {
  createdAt?: Date | string;
  requestSentAt?: Date | string | null;
  allDeclinedNotifiedAt?: Date | string | null;
  compensationSetAt?: Date | string | null;
  compensation?: string | null;
  possibleSubstitutes?: Array<{ employee?: Id; hasRejected?: boolean; rejectedAt?: Date | string | null; contactByMail?: boolean; contactByWhatsApp?: boolean; messengerMail?: unknown; messengerWhatsAppMessage?: unknown;
    requestedAt?: Date | string | null; addedByName?: string | null; addedByOffice?: boolean | null }>;
  confirmations?: Array<{ employee?: Id; confirmedAt?: Date | string | null; revokedAt?: Date | string | null; isRevoked?: boolean; isSelectedItem?: boolean; reaskedAt?: Date | string | null;
    updatedAt?: Date | string | null; enteredVia?: string | null; enteredByName?: string | null; confirmationSentAt?: Date | string | null; confirmationSkipped?: string | null;
    confirmationWhatsApp?: unknown; confirmationMail?: unknown }>;
  cancellationConfig?: {
    sendAt?: Date | string | null;
    startedCancellationAt?: Date | string | null;
    completedCancellationAt?: Date | string | null;
    autoCancelStoppedAt?: Date | string | null;
    sendAfterReview?: boolean | null;
  } | null;
  makeupOffer?: { requestedAt?: Date | string | null } | null;
}

export interface TimelineContext {
  now: Date;
  /** employee id -> "Vorname Nachname" */
  names: Record<string, string>;
  teacherName?: string;
  creatorName?: string;
  sickReport?: { reportedAt?: Date | string; reason?: string } | null;
  /** planned automatic cancellation (situation) */
  autoCancelAt?: Date | string | null;
  compensationLabel?: string;
}

/** Way of a confirmation entered by the office ("Zusage eintragen") */
export const ENTERED_VIA_LABELS: Record<string, string> = {whatsApp: 'WhatsApp', phone: 'Telefon', personal: 'persönlich', mail: 'E-Mail'};

/** "meldet sich krank" / "meldet sich ab (privat)" / "meldet sich ab" from the reason of the sick report */
export function reportVerb(reason?: string | null): string {
  if (reason === 'sick') {
    return 'meldet sich krank';
  }
  return reason === 'private' ? 'meldet sich ab (privat)' : 'meldet sich ab';
}

/** "Krank" / "Privat" / "Sonstiges" */
export const REPORT_REASON_LABELS: Record<string, string> = {sick: 'Krank', private: 'Privat', other: 'Sonstiges'};

/**
 * Head line of the panel: "Lehrer Lämpel · Krank · selbst gemeldet im Lehrkraft-Bereich am 11.10. um 11:15 · rechtzeitig";
 * office inquiries: "Lehrer Lämpel · Krankheit – Kommentar · Kurzfristig gemeldet". The automatic text of the sick report
 * ("Selbst gemeldet im Lehrkraft-Bereich (Krank)") is not repeated.
 */
export function absenceLine(input: {
  teacherName?: string;
  reasonName?: string;
  reasonText?: string;
  noticeName?: string;
  report?: { reportedAt?: Date | string; reason?: string; shortNotice?: boolean } | null;
}): string {
  const parts: string[] = [];
  if (input.teacherName) {
    parts.push(input.teacherName);
  }
  if (input.report) {
    const reason = (input.report.reason && REPORT_REASON_LABELS[input.report.reason]) || input.reasonName;
    if (reason) {
      parts.push(reason);
    }
    parts.push(input.report.reportedAt
      ? `selbst gemeldet im Lehrkraft-Bereich am ${moment(input.report.reportedAt).format('DD.MM. [um] HH:mm')}` : 'selbst gemeldet im Lehrkraft-Bereich');
    if (typeof input.report.shortNotice === 'boolean') {
      parts.push(input.report.shortNotice ? 'kurzfristig' : 'rechtzeitig');
    } else if (input.noticeName) {
      parts.push(input.noticeName);
    }
    return parts.join(' · ');
  }
  const text = (input.reasonText || '').trim();
  const reason = [input.reasonName, text].filter(Boolean).join(' – ');
  if (reason) {
    parts.push(reason);
  }
  if (input.noticeName) {
    parts.push(input.noticeName);
  }
  return parts.join(' · ');
}

/** "Verlauf" from the real timestamps of the inquiry (oldest first; planned steps at the end) */
export function buildTimeline(inquiry: TimelineInquiry, ctx: TimelineContext): TimelineEntry[] {
  const out: TimelineEntry[] = [];
  const name = (id: Id) => ctx.names[idText(id)] || 'Eine Lehrkraft';
  const add = (at: Date | string | null | undefined, text: string, tone?: OverviewTone) => {
    if (at && moment(at).isValid()) {
      out.push({at: moment(at).toDate(), text, ...(tone ? {tone} : {})});
    }
  };
  if (ctx.sickReport?.reportedAt) {
    add(ctx.sickReport.reportedAt, `${ctx.teacherName || 'Die Lehrkraft'} ${reportVerb(ctx.sickReport.reason)} (Lehrkraft-Bereich)`);
  } else {
    add(inquiry.createdAt, `Anfrage angelegt${ctx.creatorName ? ` von ${ctx.creatorName}` : ''}`);
  }
  const asked = (inquiry.possibleSubstitutes || []).filter((s) => s.contactByMail || s.contactByWhatsApp || s.messengerMail || s.messengerWhatsAppMessage);
  const viaText = (list: typeof asked) => {
    const whatsApp = list.some((s) => s.messengerWhatsAppMessage || s.contactByWhatsApp);
    const mail = list.some((s) => s.messengerMail || s.contactByMail);
    return whatsApp && mail ? 'per WhatsApp und E-Mail' : whatsApp ? 'per WhatsApp' : 'per E-Mail';
  };
  // first request: everybody without an own request time; asked later ("Weitere Lehrkräfte anfragen"): own entry
  const first = asked.filter((s) => !askedLater(s, inquiry.requestSentAt));
  if (inquiry.requestSentAt && first.length) {
    add(inquiry.requestSentAt, `${first.length === 1 ? '1 Lehrkraft' : `${first.length} Lehrkräfte`} ${viaText(first)} angefragt`);
  }
  const later = new Map<string, typeof asked>();
  for (const s of asked.filter((x) => askedLater(x, inquiry.requestSentAt))) {
    const key = `${moment(requestTimeOf(s)).format('YYYY-MM-DD HH:mm')}|${s.addedByName || ''}`;
    later.set(key, [...(later.get(key) || []), s]);
  }
  for (const group of Array.from(later.values())) {
    const by = group[0].addedByName ? ` (Büro: ${group[0].addedByName})` : ' (Büro)';
    add(requestTimeOf(group[0]), `${group.map((s) => name(s.employee)).join(', ')} zusätzlich ${viaText(group)} angefragt${by}`);
  }
  // each answer with its time
  const revokedOwn = new Set((inquiry.confirmations || []).filter((c) => c.isRevoked).map((c) => idText(c.employee)));
  for (const s of inquiry.possibleSubstitutes || []) {
    if (s.hasRejected && s.rejectedAt && !revokedOwn.has(idText(s.employee))) {
      add(s.rejectedAt, `${name(s.employee)}: „Kann ich nicht“`, 'bad');
    }
  }
  for (const c of inquiry.confirmations || []) {
    add(c.confirmedAt, c.enteredVia
      ? `${name(c.employee)}: Zusage vom Büro eingetragen (${ENTERED_VIA_LABELS[c.enteredVia] || c.enteredVia})${c.enteredByName ? ` – ${c.enteredByName}` : ''}`
      : `${name(c.employee)}: Zusage`, 'good');
    if (c.isRevoked) {
      const sub = (inquiry.possibleSubstitutes || []).find((s) => idText(s.employee) === idText(c.employee));
      if (sub?.hasRejected) {
        // time of the withdrawal = rejectedAt of the teacher (revokedAt of older data was overwritten by later saves)
        add(revokeTimeOf(c, inquiry.confirmations || [], sub.rejectedAt), `${name(c.employee)}: Zusage zurückgezogen`, 'bad');
      } else {
        add(revokeTimeOf(c, inquiry.confirmations || []), `Büro widerruft die Zusage von ${name(c.employee)}`);
      }
    }
    add(c.reaskedAt, `Warteliste erneut angefragt: ${name(c.employee)}`);
    add(c.confirmationSentAt, `Bestätigung ${confirmationChannelText(sentConfirmationChannel(c))} an ${name(c.employee)} gesendet`, 'good');
    if (c.confirmationSkipped && !c.confirmationSentAt) {
      add(c.confirmedAt, `${name(c.employee)}: ${c.confirmationSkipped}`, 'warn');
    }
  }
  const selected = (inquiry.confirmations || []).find((c) => c.isSelectedItem && !c.isRevoked);
  const plannedCfg = inquiry.cancellationConfig;
  if (selected && plannedCfg?.sendAt && !plannedCfg.autoCancelStoppedAt && !plannedCfg.startedCancellationAt
    && !plannedCfg.completedCancellationAt && !plannedCfg.sendAfterReview) {
    add(selected.confirmedAt, 'Automatische Absage entfällt – Vertretung steht');
  }
  add(inquiry.allDeclinedNotifiedAt, 'Büro und QM informiert – To-do angelegt');
  const cfg = inquiry.cancellationConfig;
  add(cfg?.autoCancelStoppedAt, 'Automatische Absage gestoppt – Büro entscheidet', 'warn');
  add(cfg?.startedCancellationAt, 'Absage wird an die Eltern versendet');
  add(cfg?.completedCancellationAt, 'Stunde abgesagt – Eltern informiert');
  add(inquiry.compensationSetAt, ctx.compensationLabel ? `Kompensation festgelegt: ${ctx.compensationLabel}` : 'Kompensation festgelegt');
  add(inquiry.makeupOffer?.requestedAt, 'Online-Nachholen angeboten');
  out.sort((a, b) => a.at.valueOf() - b.at.valueOf());
  if (ctx.autoCancelAt && moment(ctx.autoCancelAt).isAfter(moment(ctx.now))) {
    out.push({at: moment(ctx.autoCancelAt).toDate(), text: 'geplant: automatische Absage an die Eltern', planned: true, tone: 'bad'});
  }
  return out;
}

/* ================================================================ links */

/** Office overview opened at one inquiry (bells, to-dos) */
export function inquiryPath(id: unknown): string {
  return `/substitution-inquiries-table?inquiry=${encodeURIComponent(idText(id))}`;
}

/** Office overview filtered to the lessons of one sick report (all days) */
export function sickReportPath(id: unknown): string {
  return `/substitution-inquiries-table?q=1&filter.sickReport=${encodeURIComponent(JSON.stringify([idText(id)]))}`;
}

/* ================================================================ situation of a loaded inquiry (panel) */

export interface SituationSource {
  day: Date | string;
  state?: number;
  requestSentAt?: Date | string | null;
  reportedOnly?: boolean | null;
  autoCancelPlaceholder?: boolean | null;
  compensation?: string | null;
  possibleSubstitutes?: Array<{ employee?: unknown; hasRejected?: boolean; contactByMail?: boolean; contactByWhatsApp?: boolean; messengerMail?: unknown; messengerWhatsAppMessage?: unknown }>;
  confirmations?: Array<{ employee?: unknown; isRevoked?: boolean; isSelectedItem?: boolean }>;
  cancellationConfig?: {
    sendAt?: Date | string | null;
    startedCancellationAt?: Date | string | null;
    completedCancellationAt?: Date | string | null;
    sendAfterReview?: boolean | null;
    autoCancelStoppedAt?: Date | string | null;
    cancellationCompensated?: boolean | null;
  } | null;
}

/** CANCELED of SubstitutionInquiryState */
const CANCELED = 2;

/** Same rules as the substitution-manager (overview.rules.ts overviewSituation) for an inquiry loaded by id */
export function clientSituation(inquiry: SituationSource, now: Date): SubstitutionOverviewSituation {
  const cfg = inquiry.cancellationConfig;
  const confirmations = inquiry.confirmations || [];
  if (inquiry.state === CANCELED || cfg?.completedCancellationAt) {
    return !inquiry.compensation && !cfg?.cancellationCompensated ? S.COMPENSATION_OPEN : S.CANCELLED;
  }
  if (cfg?.startedCancellationAt) {
    return S.CANCELLING;
  }
  if (cfg?.sendAfterReview) {
    return S.CANCEL_NOT_SENT;
  }
  if (confirmations.some((c) => c.isSelectedItem && !c.isRevoked)) {
    return S.FOUND;
  }
  if (confirmations.some((c) => !c.isRevoked)) {
    return S.CHOOSE;
  }
  if (moment(inquiry.day).isBefore(moment(now), 'day')) {
    return S.PAST;
  }
  if (inquiry.reportedOnly) {
    return S.REPORTED_ONLY;
  }
  const subs = inquiry.possibleSubstitutes || [];
  if (!inquiry.requestSentAt && subs.some((s) => s.contactByMail || s.contactByWhatsApp)) {
    return S.NOT_SENT;
  }
  const asked = subs.filter((s) => s.contactByMail || s.contactByWhatsApp || s.messengerMail || s.messengerWhatsAppMessage
    || (s as { addedByOffice?: boolean }).addedByOffice);
  if (inquiry.autoCancelPlaceholder || !asked.length) {
    return S.NO_CANDIDATES;
  }
  if (asked.every((s) => s.hasRejected)) {
    const revoked = new Set(confirmations.filter((c) => c.isRevoked).map((c) => idText(c.employee)));
    return asked.some((s) => s.hasRejected && revoked.has(idText(s.employee))) ? S.WITHDRAWN : S.ALL_DECLINED;
  }
  return S.SEARCHING;
}

/** Planned automatic cancellation (not stopped / started / after review, day not over) */
export function plannedAutoCancel(inquiry: SituationSource, now: Date): Date | undefined {
  const cfg = inquiry.cancellationConfig;
  if (!cfg?.sendAt || cfg.startedCancellationAt || cfg.completedCancellationAt || cfg.sendAfterReview || cfg.autoCancelStoppedAt) {
    return undefined;
  }
  if (moment(inquiry.day).isBefore(moment(now), 'day')) {
    return undefined;
  }
  // a substitute is confirmed: the automatic cancellation does not run (comes back when the confirmation is revoked)
  if ((inquiry.confirmations || []).some((c) => c.isSelectedItem && !c.isRevoked)) {
    return undefined;
  }
  return moment(cfg.sendAt).toDate();
}

/* ================================================================ "Was ist zu tun?" */

export type DetailAction = 'send' | 'search' | 'ask' | 'cancel' | 'stop' | 'choose' | 'reask' | 'sendCancellation';

export interface SituationBox {
  title: string;
  text: string;
  tone: OverviewTone;
  countdown?: string;
  actions: DetailAction[];
}

export interface SituationBoxContext {
  now: Date;
  autoCancelAt?: Date;
  autoCancelStopped: boolean;
  /** Somebody is on the waiting list after the office revoked the selected confirmation */
  waitingListFree: boolean;
  studentCount?: number;
  /** "eine WhatsApp" / "eine E-Mail" ('' unknown) */
  parentsChannel?: string;
  substituteName?: string;
}

/**
 * Box "Was ist zu tun?" of the panel with exactly the actions of the situation (no second cancel button elsewhere):
 * search / ask more teachers (info), cancel now (error), stop the automatic cancellation (warning) …
 */
export function situationBox(situation: SubstitutionOverviewSituation, ctx: SituationBoxContext): SituationBox | undefined {
  const kids = ctx.studentCount ? ` der ${ctx.studentCount === 1 ? '1 Kind' : `${ctx.studentCount} Kinder`}`.replace('der 1 Kind', 'des Kindes') : '';
  const parents = ctx.parentsChannel ? ` – die Eltern${kids} bekommen ${ctx.parentsChannel}` : '';
  const auto = ctx.autoCancelAt
    ? `Wenn niemand zusagt, sagt Vanilla die Stunde am ${dateTimeText(ctx.autoCancelAt)} automatisch ab${parents}.`
    : ctx.autoCancelStopped
      ? 'Die automatische Absage wurde gestoppt – ohne Entscheidung findet die Stunde ohne Lehrkraft statt.'
      : 'Es ist keine automatische Absage geplant – ohne Entscheidung findet die Stunde ohne Lehrkraft statt.';
  const stop: DetailAction[] = ctx.autoCancelAt ? ['stop'] : [];
  const countdown = ctx.autoCancelAt ? `Absage ${countdownIn(ctx.autoCancelAt, ctx.now)}` : undefined;
  const reask: DetailAction[] = ctx.waitingListFree ? ['reask'] : [];
  switch (situation) {
    case S.NOT_SENT:
      return {title: 'Anfrage noch nicht versendet', text: `Die Lehrkräfte wissen noch nichts von der Anfrage. ${ctx.autoCancelAt ? auto : ''}`.trim(),
        tone: 'warn', countdown, actions: ['send', 'cancel', ...stop]};
    case S.REPORTED_ONLY:
      return {title: 'Ausfall gemeldet – noch keine Suche',
        text: 'Die Lehrkraft hat den Ausfall gemeldet. Es wurde noch niemand angefragt und es ist keine automatische Absage geplant – ohne Entscheidung findet die Stunde ohne Lehrkraft statt.',
        tone: 'warn', actions: ['search', 'cancel']};
    case S.NO_CANDIDATES:
      return {title: 'Keine Vertretung gefunden', text: `Niemand konnte angefragt werden. ${auto}`, tone: 'bad', countdown, actions: ['search', 'cancel', ...stop]};
    case S.ALL_DECLINED:
      return {title: 'Alle Angefragten haben abgesagt', text: auto, tone: 'bad', countdown, actions: [...reask, 'ask', 'cancel', ...stop]};
    case S.WITHDRAWN:
      return {title: 'Vertretung zurückgezogen – niemand mehr offen', text: auto, tone: 'warn', countdown, actions: [...reask, 'ask', 'cancel', ...stop]};
    case S.SEARCHING:
      return {title: 'Suche läuft', text: ctx.waitingListFree ? `Die Vertretung ist wieder frei. ${auto}` : auto, tone: 'info', countdown,
        actions: [...reask, 'ask', 'cancel', ...stop]};
    case S.CHOOSE:
      return {title: 'Zusage liegt vor', text: 'Bitte unter „Angefragt“ als Vertretung auswählen (Menü der Person). Bis dahin ist die Stunde nicht besetzt.',
        tone: 'warn', actions: ['choose']};
    case S.CANCEL_NOT_SENT:
      return {title: 'Absage noch nicht versendet', text: 'Die Absage ist gespeichert. Standort und Eltern erfahren erst mit dem Senden vom Ausfall.',
        tone: 'warn', actions: ['sendCancellation']};
    case S.FOUND:
      return {title: 'Vertretung steht', text: ctx.substituteName ? `${ctx.substituteName} vertritt.` : 'Die Stunde ist vertreten.', tone: 'good', actions: []};
    case S.COMPENSATION_OPEN:
      return {title: 'Abgesagt – Kompensation offen', text: 'Bitte unten unter „Grund und Kompensation“ festlegen, wie die Stunde ausgeglichen wird.', tone: 'comp', actions: []};
    case S.CANCELLING:
      return {title: 'Absage wird versendet', text: 'Eltern und Standort werden gerade informiert.', tone: 'muted', actions: []};
    case S.CANCELLED:
      return {title: 'Abgesagt', text: 'Die Stunde ist abgesagt.', tone: 'muted', actions: []};
    case S.PAST:
      return {title: 'Vorbei – ohne Vertretung', text: 'Der Tag ist vorbei. Es wurde weder eine Vertretung gefunden noch abgesagt.', tone: 'muted', actions: []};
    default:
      return undefined;
  }
}

export const DETAIL_ACTION_TEXTS: Record<DetailAction, string> = {
  send: 'Prüfen und senden',
  search: 'Vertretung suchen',
  ask: 'Weitere Lehrkräfte anfragen',
  cancel: 'Jetzt absagen',
  stop: 'Automatische Absage stoppen',
  choose: 'Erste Zusage als Vertretung auswählen',
  reask: 'Warteliste erneut anfragen',
  sendCancellation: 'Absage prüfen und senden',
};

/** Vanilla button colors: info = primary actions, warning, error */
export const DETAIL_ACTION_COLORS: Record<DetailAction, 'info' | 'warning' | 'error'> = {
  send: 'info',
  search: 'info',
  ask: 'info',
  cancel: 'error',
  stop: 'warning',
  choose: 'info',
  reask: 'info',
  sendCancellation: 'info',
};

/** Icons of the actions (modern CTA buttons) */
export const DETAIL_ACTION_ICONS: Record<DetailAction, string> = {
  send: 'mdi-send-outline',
  search: 'mdi-account-search-outline',
  ask: 'mdi-account-plus-outline',
  cancel: 'mdi-cancel',
  stop: 'mdi-timer-off-outline',
  choose: 'mdi-check',
  reask: 'mdi-human-queue',
  sendCancellation: 'mdi-email-check-outline',
};
